import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { AssistantSettings, VoiceCatalog } from "../api/types.js";
import { useSession } from "./session.js";

interface AssistantValue {
  /** Null until the first load finishes (or when it failed). */
  settings: AssistantSettings | null;
  catalog: VoiceCatalog | null;
  /** Saves a patch and resolves with the stored settings; rejects with the API error. */
  save: (patch: Partial<AssistantSettings>) => Promise<AssistantSettings>;
  /** Display name with a sensible fallback while loading. */
  name: string;
}

const Ctx = createContext<AssistantValue | null>(null);

/** Loads the user's assistant settings and voice catalog once for the whole signed-in shell. */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const { services, user } = useSession();
  const [settings, setSettings] = useState<AssistantSettings | null>(null);
  const [catalog, setCatalog] = useState<VoiceCatalog | null>(null);

  useEffect(() => {
    let cancelled = false;
    services.api
      .getAssistantSettings()
      .then((s) => !cancelled && setSettings(s))
      .catch(() => undefined);
    services.api
      .listVoices()
      .then((c) => !cancelled && setCatalog(c))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [services, user?.id]);

  const save = useCallback(
    async (patch: Partial<AssistantSettings>) => {
      const next = { ...(settings as AssistantSettings), ...patch };
      const stored = await services.api.saveAssistantSettings(next);
      setSettings(stored);
      return stored;
    },
    [services, settings],
  );

  const value = useMemo<AssistantValue>(
    () => ({ settings, catalog, save, name: settings?.name ?? "" }),
    [settings, catalog, save],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAssistant(): AssistantValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAssistant must be used inside <AssistantProvider>");
  return v;
}
